import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { DatePicker } from '@/components/ui/date-picker';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import type { ReportFilterOptions } from '@/hooks/useReportFilterOptions';
import type { PostingStatus } from '@/utils/PostingLog/types';
import { STATUS_OPTIONS } from '@/utils/PostingLog/constants';
import type { PostingLogFiltersState } from '@/hooks/PostingLog/usePostingLogFilters';

interface PostingLogFilterSheetProps {
  filters: PostingLogFiltersState;
  filterOptions: ReportFilterOptions;
}

export const PostingLogFilterSheet: React.FC<PostingLogFilterSheetProps> = ({ filters, filterOptions }) => {
  const {
    workingFilters,
    setWorkingFilters,
    showFilterSheet,
    setShowFilterSheet,
    handleApplySheet,
  } = filters;
  const employeeOptions = filterOptions.employees.map((e) => ({ value: e.id.toString(), label: e.name }));
  const projectOptions  = filterOptions.projects.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` }));
  const ccOptions       = filterOptions.costCentres.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }));
  const deptOptions     = filterOptions.departments.map((d) => ({ value: d, label: d }));

  return (
    <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
      <SheetContent side="right" className="w-full sm:w-[400px] sm:max-w-none flex flex-col p-0 gap-0">
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <SheetTitle className="text-base font-semibold">Filters</SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-5 space-y-5">
              {/* Employee */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Employee</Label>
                <MultiSelect
                  options={employeeOptions}
                  value={(workingFilters.employeeIds ?? []).map(String)}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, employeeIds: vals.length ? vals.map(Number) : undefined }))
                  }
                  placeholder="All employees" maxDisplay={2} showSearch
                />
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Date From</Label>
                  <DatePicker
                    date={workingFilters.dateFrom}
                    onDateChange={(d) => setWorkingFilters((f) => ({ ...f, dateFrom: d }))}
                    placeholder="Pick a date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Date To</Label>
                  <DatePicker
                    date={workingFilters.dateTo}
                    onDateChange={(d) => setWorkingFilters((f) => ({ ...f, dateTo: d }))}
                    placeholder="Pick a date"
                    disabled={(date) => (workingFilters.dateFrom ? date < workingFilters.dateFrom : false)}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Status</Label>
                <MultiSelect
                  options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
                  value={workingFilters.statuses ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, statuses: vals.length ? (vals as PostingStatus[]) : undefined }))
                  }
                  placeholder="All statuses" maxDisplay={2} showSearch={false}
                />
              </div>

              {/* Project */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Project</Label>
                <MultiSelect
                  options={projectOptions}
                  value={workingFilters.projectIds ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, projectIds: vals.length ? vals : undefined }))
                  }
                  placeholder="All projects" maxDisplay={2} showSearch
                />
              </div>

              {/* Cost Centre */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Cost Centre</Label>
                <MultiSelect
                  options={ccOptions}
                  value={workingFilters.costCentreIds ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, costCentreIds: vals.length ? vals : undefined }))
                  }
                  placeholder="All cost centres" maxDisplay={2} showSearch
                />
              </div>

              {/* Department */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Department</Label>
                <MultiSelect
                  options={deptOptions}
                  value={workingFilters.departments ?? []}
                  onValueChange={(vals) =>
                    setWorkingFilters((f) => ({ ...f, departments: vals.length ? vals : undefined }))
                  }
                  placeholder="All departments" maxDisplay={2} showSearch={false}
                />
              </div>

              <Separator />

              {/* Failed Only toggle */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="sheet-failed-only"
                  checked={workingFilters.failedOnly ?? false}
                  onCheckedChange={(checked) =>
                    setWorkingFilters((f) => ({ ...f, failedOnly: checked === true ? true : undefined }))
                  }
                />
                <Label htmlFor="sheet-failed-only" className="text-sm cursor-pointer">Failed Only</Label>
              </div>
            </div>
          </ScrollAreaViewport>
        </ScrollArea>
        <SheetFooter className="px-6 py-4 border-t flex-shrink-0 flex-row gap-2">
          <Button onClick={handleApplySheet} className="flex-1">Apply Filters</Button>
          <Button variant="outline" onClick={() => setWorkingFilters({})}>Clear</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
